import { useEffect, useState } from 'react'
import './legacy.css'
import { checkSupabaseConnection } from './lib/supabase'
import { activeTenant, applyTenantBranding } from './lib/tenant'
import { hydratePublicAvailability } from './features/availability/hydrateAvailability'
import { loadPublicAvailability } from './features/availability/service'
import type { PublicAvailabilitySnapshot } from './features/availability/types'
import { CommercialRequestBridge } from './features/leads/CommercialRequestBridge'
import { ClientPortalPayments } from './features/leads/ClientPortalPayments'
import { ClientPortalBridge } from './features/clients/ClientPortalBridge'
import { BrokerPortalBridge } from './features/brokers/BrokerPortalBridge'
import { PublicAppointmentBridge } from './features/appointments/PublicAppointmentBridge'
import { hydrateSiteContent } from './features/cms/hydrateSiteContent'
import { loadPublicSiteContent } from './features/cms/siteContent'
import type { SiteContent } from './features/cms/siteContent'

export default function LegacyParity() {
  const [markup, setMarkup] = useState('')
  const [availabilitySnapshot, setAvailabilitySnapshot] = useState<PublicAvailabilitySnapshot | null>(null)
  const [siteContent, setSiteContent] = useState<SiteContent | null>(null)

  useEffect(() => {
    void checkSupabaseConnection().then((status) => {
      document.documentElement.dataset.supabase = status.connected
        ? 'connected'
        : status.configured
          ? 'error'
          : 'unconfigured'
      window.dispatchEvent(new CustomEvent('siga:supabase-status', { detail: status }))
    })
  }, [])

  useEffect(() => {
    let active = true
    void loadPublicSiteContent().then(content => {
      if (active) setSiteContent(content)
    })
    return () => { active = false }
  }, [])

  useEffect(() => {
    let active = true
    void loadPublicAvailability().then(snapshot => {
      if (active) setAvailabilitySnapshot(snapshot)
    })
    return () => { active = false }
  }, [])

  useEffect(() => {
    if (markup) applyTenantBranding(activeTenant)
  }, [markup])

  useEffect(() => {
    if (markup && availabilitySnapshot && siteContent) {
      hydratePublicAvailability(availabilitySnapshot, siteContent)
    }
  }, [markup, availabilitySnapshot, siteContent])

  useEffect(() => {
    if (markup && siteContent) {
      hydrateSiteContent(siteContent)
    }
  }, [markup, siteContent])

  useEffect(() => {
    let active = true
    fetch('/legacy-content.html')
      .then(response => {
        if (!response.ok) throw new Error('No se pudo cargar la interfaz base.')
        return response.text()
      })
      .then(content => { if (active) setMarkup(content) })
      .catch(() => { if (active) setMarkup('<main class="container app-page"><h1>No se pudo cargar la interfaz.</h1></main>') })
    return () => { active = false }
  }, [])

  useEffect(() => {
    if (!markup) return
    document.body.className = ''
    const loadRuntime = () => {
      const runtime = document.createElement('script')
      runtime.id = 'legacy-runtime'
      runtime.src = '/legacy-runtime.js?v=20260921-multitenant'
      runtime.async = false
      document.body.appendChild(runtime)
    }
    const existingLucide = document.getElementById('legacy-lucide')
    if (existingLucide) {
      loadRuntime()
    } else {
      const lucide = document.createElement('script')
      lucide.id = 'legacy-lucide'
      lucide.src = '/vendor/lucide.min.js'
      lucide.async = false
      lucide.onload = loadRuntime
      document.body.appendChild(lucide)
    }
    return () => {
      document.getElementById('legacy-runtime')?.remove()
      document.body.className = ''
    }
  }, [markup])

  return (
    <>
      <div dangerouslySetInnerHTML={{ __html: markup }} />
      <CommercialRequestBridge />
      <ClientPortalPayments />
      <ClientPortalBridge ready={Boolean(markup)} />
      <BrokerPortalBridge ready={Boolean(markup)} />
      <PublicAppointmentBridge ready={Boolean(markup)} />
    </>
  )
}
